import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  generateTelegramLink, getTelegramStatus, unlinkTelegram, 
  sendTestNotification, subscribePushNotifications,
  getFaceReference, registerFaceReference, deleteFaceReference,
  changePassword
} from '../../lib/api';
import { getRoleLabel } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import FaceVerification from '../ui/FaceVerification';
import { 
  ChevronLeft, User, Bell, MessageCircle, Copy, Check,
  Loader2, ExternalLink, Unlink, Send, Smartphone, ShieldCheck, 
  UserCheck, Trash2, Camera, Key, Eye, EyeOff
} from 'lucide-react';
import { toast } from '../ui/sonner';

const ProfilPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  // Telegram states
  const [telegramStatus, setTelegramStatus] = useState({ is_linked: false });
  const [telegramLink, setTelegramLink] = useState(null);
  const [loadingTelegram, setLoadingTelegram] = useState(true);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Push notification states
  const [pushSupported, setPushSupported] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  
  // Face verification states
  const [faceData, setFaceData] = useState(null);
  const [loadingFace, setLoadingFace] = useState(true);
  const [showFaceRegistration, setShowFaceRegistration] = useState(false);
  const [savingFace, setSavingFace] = useState(false);
  
  // Password change states
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  useEffect(() => {
    loadTelegramStatus();
    checkPushSupport();
    loadFaceReference();
  }, []);
  
  const loadFaceReference = async () => {
    try {
      setLoadingFace(true);
      const data = await getFaceReference();
      setFaceData(data);
    } catch (error) {
      console.error('Failed to load face reference:', error);
    } finally {
      setLoadingFace(false);
    }
  };
  
  const handleFaceRegistration = async (result) => {
    setSavingFace(true);
    try {
      await registerFaceReference({
        foto_base64: result.photo,
        face_descriptor: result.descriptor
      });
      
      toast.success('Wajah berhasil didaftarkan! 🎉', {
        description: 'Verifikasi wajah akan digunakan saat absensi.'
      });
      
      setShowFaceRegistration(false);
      loadFaceReference();
    } catch (error) {
      console.error('Face registration error:', error);
      toast.error('Gagal menyimpan data wajah');
    } finally {
      setSavingFace(false);
    }
  };
  
  const handleDeleteFace = async () => {
    if (!window.confirm('Hapus data wajah? Anda perlu mendaftarkan ulang untuk verifikasi wajah.')) {
      return;
    }
    
    try {
      await deleteFaceReference();
      setFaceData({ has_reference: false });
      toast.success('Data wajah berhasil dihapus');
    } catch (error) {
      toast.error('Gagal menghapus data wajah');
    }
  };

  const loadTelegramStatus = async () => {
    try {
      setLoadingTelegram(true);
      const status = await getTelegramStatus();
      setTelegramStatus(status);
    } catch (error) {
      console.error('Failed to load Telegram status:', error);
    } finally {
      setLoadingTelegram(false);
    }
  };

  const checkPushSupport = () => {
    const supported = 'Notification' in window && 'serviceWorker' in navigator;
    setPushSupported(supported);
    if (supported) {
      setPushSubscribed(Notification.permission === 'granted');
    }
  };

  const handleGenerateLink = async () => {
    setGeneratingLink(true);
    try {
      const link = await generateTelegramLink();
      setTelegramLink(link);
    } catch (error) {
      toast.error('Gagal generate link');
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleCopyCode = () => {
    if (telegramLink?.code) {
      navigator.clipboard.writeText(telegramLink.code);
      setCopied(true);
      toast.success('Kode disalin!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleUnlinkTelegram = async () => {
    try {
      await unlinkTelegram();
      setTelegramStatus({ is_linked: false });
      setTelegramLink(null);
      toast.success('Telegram berhasil dihapus');
    } catch (error) {
      toast.error('Gagal menghapus Telegram');
    }
  };

  const handleSendTest = async () => {
    try {
      await sendTestNotification();
      toast.success('Notifikasi test dikirim ke Telegram');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengirim notifikasi');
    }
  };

  const handleEnablePush = async () => {
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        // Register service worker and get subscription
        const registration = await navigator.serviceWorker.ready;
        
        // Create a simple subscription object
        const subscription = {
          endpoint: 'browser-push',
          enabled: true,
          timestamp: new Date().toISOString()
        };
        
        await subscribePushNotifications(subscription);
        setPushSubscribed(true);
        toast.success('Notifikasi browser diaktifkan');
      } else {
        toast.error('Izin notifikasi ditolak');
      }
    } catch (error) {
      toast.error('Gagal mengaktifkan notifikasi');
    }
  };

  const openTelegramBot = () => {
    const botUsername = telegramLink?.bot_username || 'SigapGuruBot';
    const startParam = telegramLink?.code || '';
    window.open(`https://t.me/${botUsername}?start=${startParam}`, '_blank');
  };
  
  // Handle password change
  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Semua field harus diisi');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Password baru dan konfirmasi tidak cocok');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password minimal 6 karakter');
      return;
    }
    
    setChangingPassword(true);
    try {
      await changePassword(currentPassword, newPassword, confirmPassword);
      toast.success('Password berhasil diubah! 🎉');
      setShowPasswordChange(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Gagal mengubah password';
      toast.error(errorMsg);
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-2xl mx-auto" data-testid="profil-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-indigo-950">Profil & Pengaturan</h1>
          <p className="text-gray-500">Kelola akun dan notifikasi</p>
        </div>
      </div>

      {/* User Info Card */}
      <Card className="mb-6 border-indigo-100/50">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center overflow-hidden">
              {faceData?.has_reference && faceData?.foto_wajah_referensi ? (
                <img 
                  src={`data:image/jpeg;base64,${faceData.foto_wajah_referensi}`}
                  alt="Foto Wajah"
                  className="w-full h-full object-cover"
                />
              ) : (
                <User className="w-8 h-8 text-indigo-600" />
              )}
            </div>
            <div>
              <h2 className="text-xl font-bold text-indigo-950">{user?.nama}</h2>
              <p className="text-gray-500 font-mono">{user?.nip}</p>
              <Badge className="mt-1 bg-indigo-100 text-indigo-800">
                {getRoleLabel(user?.role)}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Face Verification Card - Only show for roles that need attendance */}
      {['guru', 'kepala_sekolah', 'pengawas', 'tenaga_tata_usaha', 'pegawai_instansi'].includes(user?.role) && (
        <Card className="mb-6 border-indigo-100/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
              Verifikasi Wajah
            </CardTitle>
            <CardDescription>
              Daftarkan wajah untuk keamanan absensi
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingFace ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
              </div>
            ) : showFaceRegistration ? (
              <div className="space-y-4">
                <FaceVerification
                  mode="register"
                  onRegistrationComplete={handleFaceRegistration}
                  onCancel={() => setShowFaceRegistration(false)}
                />
                {savingFace && (
                  <div className="flex items-center justify-center gap-2 text-indigo-600">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Menyimpan data wajah...</span>
                  </div>
                )}
              </div>
            ) : faceData?.has_reference ? (
              <div className="space-y-4">
                <Alert className="border-emerald-200 bg-emerald-50">
                  <Check className="w-4 h-4 text-emerald-600" />
                  <AlertDescription className="text-emerald-800">
                    <span className="font-medium">Wajah sudah terdaftar!</span>
                    <br />
                    <span className="text-sm">
                      Terdaftar pada: {faceData.registered_at ? new Date(faceData.registered_at).toLocaleDateString('id-ID', {
                        day: 'numeric',
                        month: 'long', 
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : '-'}
                    </span>
                  </AlertDescription>
                </Alert>
                
                {faceData.foto_wajah_referensi && (
                  <div className="flex justify-center">
                    <div className="relative w-32 h-32 rounded-lg overflow-hidden border-2 border-emerald-200">
                      <img 
                        src={`data:image/jpeg;base64,${faceData.foto_wajah_referensi}`}
                        alt="Foto Referensi"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-emerald-500/90 text-white text-xs py-1 text-center">
                        <UserCheck className="w-3 h-3 inline mr-1" />
                        Terverifikasi
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowFaceRegistration(true)}
                    className="flex-1 border-indigo-200 text-indigo-700"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Daftar Ulang
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleDeleteFace}
                    className="flex-1 border-red-200 text-red-700"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Hapus
                  </Button>
                </div>
                
                <p className="text-sm text-gray-500 text-center">
                  Wajah akan diverifikasi setiap kali melakukan absensi
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-800">
                    <strong>⚠️ Wajah belum terdaftar</strong>
                  </p>
                  <p className="text-sm text-amber-700 mt-1">
                    Daftarkan wajah Anda untuk meningkatkan keamanan absensi dan mencegah absensi oleh orang lain.
                  </p>
                </div>
                
                <div className="p-4 bg-indigo-50 rounded-lg">
                  <p className="text-sm text-indigo-800 font-medium mb-2">
                    Manfaat verifikasi wajah:
                  </p>
                  <ul className="text-sm text-indigo-700 space-y-1">
                    <li>• Mencegah titip absen (buddy punching)</li>
                    <li>• Memastikan kehadiran yang sah</li>
                    <li>• Meningkatkan integritas data kehadiran</li>
                  </ul>
                </div>

                <Button
                  onClick={() => setShowFaceRegistration(true)}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                >
                  <UserCheck className="w-4 h-4 mr-2" />
                  Daftarkan Wajah Saya
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Telegram Integration */}
      <Card className="mb-6 border-indigo-100/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-blue-500" />
            Notifikasi Telegram
          </CardTitle>
          <CardDescription>
            Terima pengingat absensi otomatis via Telegram
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingTelegram ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
            </div>
          ) : telegramStatus.is_linked ? (
            <div className="space-y-4">
              <Alert className="border-emerald-200 bg-emerald-50">
                <Check className="w-4 h-4 text-emerald-600" />
                <AlertDescription className="text-emerald-800">
                  Telegram sudah terhubung
                </AlertDescription>
              </Alert>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={handleSendTest}
                  className="flex-1 border-blue-200 text-blue-700"
                  data-testid="send-test-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Kirim Test
                </Button>
                <Button
                  variant="outline"
                  onClick={handleUnlinkTelegram}
                  className="flex-1 border-red-200 text-red-700"
                  data-testid="unlink-telegram-btn"
                >
                  <Unlink className="w-4 h-4 mr-2" />
                  Hapus
                </Button>
              </div>
              
              <p className="text-sm text-gray-500 text-center">
                Anda akan menerima notifikasi pada pukul 06:30 dan 13:30 WIB
              </p>
            </div>
          ) : telegramLink ? (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg text-center">
                <p className="text-sm text-gray-500 mb-2">Kode Verifikasi:</p>
                <div className="flex items-center justify-center gap-2">
                  <code className="text-2xl font-bold text-indigo-950 tracking-wider">
                    {telegramLink.code}
                  </code>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleCopyCode}
                  >
                    {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Berlaku hingga 10 menit
                </p>
              </div>

              <Button
                onClick={openTelegramBot}
                className="w-full bg-blue-500 hover:bg-blue-600"
                data-testid="open-telegram-btn"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Buka Telegram Bot
              </Button>

              <div className="text-sm text-gray-500 space-y-1">
                <p className="font-medium">Cara menghubungkan:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Klik tombol &quot;Buka Telegram Bot&quot;</li>
                  <li>Klik START di Telegram</li>
                  <li>Bot akan otomatis terhubung</li>
                </ol>
              </div>

              <Button
                variant="link"
                onClick={() => setTelegramLink(null)}
                className="w-full text-gray-500"
              >
                Batal
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-gray-600">
                Hubungkan akun Telegram untuk menerima pengingat absensi otomatis setiap hari.
              </p>
              
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Notifikasi yang akan diterima:</strong>
                </p>
                <ul className="mt-2 text-sm text-blue-700 space-y-1">
                  <li>• Pengingat absen masuk (06:30 WIB)</li>
                  <li>• Pengingat absen pulang (13:30 WIB)</li>
                  <li>• Pengingat foto kegiatan</li>
                </ul>
              </div>

              <Button
                onClick={handleGenerateLink}
                disabled={generatingLink}
                className="w-full bg-blue-500 hover:bg-blue-600"
                data-testid="connect-telegram-btn"
              >
                {generatingLink ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <MessageCircle className="w-4 h-4 mr-2" />
                )}
                Hubungkan Telegram
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Change Password - For all staff roles */}
      {['guru', 'kepala_sekolah', 'tenaga_tata_usaha', 'pegawai_instansi'].includes(user?.role) && (
        <Card className="mb-6 border-indigo-100/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-600" />
              Ubah Password
            </CardTitle>
            <CardDescription>
              Ganti password akun Anda untuk keamanan
            </CardDescription>
          </CardHeader>
          <CardContent>
            {showPasswordChange ? (
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Password Saat Ini</Label>
                  <div className="relative">
                    <Input
                      id="currentPassword"
                      type={showCurrentPassword ? "text" : "password"}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Masukkan password saat ini"
                      data-testid="current-password-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="newPassword">Password Baru</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Minimal 6 karakter"
                      data-testid="new-password-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Konfirmasi Password Baru</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Ulangi password baru"
                    data-testid="confirm-password-input"
                  />
                </div>
                
                <div className="flex gap-3">
                  <Button
                    type="submit"
                    disabled={changingPassword}
                    className="flex-1 bg-amber-600 hover:bg-amber-700"
                    data-testid="submit-password-btn"
                  >
                    {changingPassword ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Check className="w-4 h-4 mr-2" />
                    )}
                    Simpan Password
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowPasswordChange(false);
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                    }}
                    className="flex-1"
                  >
                    Batal
                  </Button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <p className="text-gray-600 text-sm">
                  Pastikan password Anda kuat dan tidak mudah ditebak untuk menjaga keamanan akun.
                </p>
                <Button
                  onClick={() => setShowPasswordChange(true)}
                  variant="outline"
                  className="w-full border-amber-200 text-amber-700 hover:bg-amber-50"
                  data-testid="change-password-btn"
                >
                  <Key className="w-4 h-4 mr-2" />
                  Ubah Password
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Push Notifications */}
      <Card className="mb-6 border-indigo-100/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-amber-500" />
            Notifikasi Browser
          </CardTitle>
          <CardDescription>
            Terima notifikasi langsung di browser
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!pushSupported ? (
            <Alert className="border-gray-200 bg-gray-50">
              <Smartphone className="w-4 h-4" />
              <AlertDescription>
                Browser Anda tidak mendukung notifikasi push
              </AlertDescription>
            </Alert>
          ) : pushSubscribed ? (
            <div className="space-y-4">
              <Alert className="border-emerald-200 bg-emerald-50">
                <Check className="w-4 h-4 text-emerald-600" />
                <AlertDescription className="text-emerald-800">
                  Notifikasi browser aktif
                </AlertDescription>
              </Alert>
              <p className="text-sm text-gray-500 text-center">
                Anda akan menerima notifikasi reminder di browser
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-gray-600 text-sm">
                Aktifkan notifikasi browser untuk menerima pengingat secara real-time.
              </p>
              <Button
                onClick={handleEnablePush}
                variant="outline"
                className="w-full border-amber-200 text-amber-700"
                data-testid="enable-push-btn"
              >
                <Bell className="w-4 h-4 mr-2" />
                Aktifkan Notifikasi Browser
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Logout */}
      <Button
        variant="outline"
        onClick={() => {
          logout();
          navigate('/login');
        }}
        className="w-full border-red-200 text-red-600 hover:bg-red-50"
        data-testid="logout-btn"
      >
        Keluar dari Akun
      </Button>
    </div>
  );
};

export default ProfilPage;
